import axios, { type AxiosError, type InternalAxiosRequestConfig, isAxiosError } from 'axios';
import { env } from './env';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

export const api = axios.create({
  baseURL: env.VITE_API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

api.interceptors.request.use((config) => {
  const method = config.method?.toUpperCase();
  if (method && method !== 'GET' && method !== 'HEAD') {
    const csrf = readCookie('__Host-nt_csrf');
    if (csrf) config.headers.set('X-CSRF-Token', csrf);
  }
  return config;
});

let refreshPromise: Promise<void> | null = null;
function tryRefresh(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${env.VITE_API_URL}/auth/refresh`, null, { withCredentials: true })
      .then(() => undefined)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const config = error.config as InternalAxiosRequestConfig | undefined;
    const url = config?.url ?? '';
    const skipRefresh = url.endsWith('/auth/login') || url.endsWith('/auth/logout');
    if (error.response?.status === 401 && config && !config._retry && !skipRefresh) {
      config._retry = true;
      try {
        await tryRefresh();
        return api.request(config);
      } catch {
        // Já em /login não redireciona: o probe `GET /me` responde 401 quando deslogado e, sem
        // esta guarda, o replace recarrega /login em loop. Fora de /login, manda pro login.
        if (window.location.pathname !== '/login') {
          window.location.replace(`/login?from=${encodeURIComponent(window.location.pathname)}`);
        }
      }
    }
    return Promise.reject(error);
  },
);

/** Arquivo baixado; `fileName` vem do `Content-Disposition` e pode faltar (proxy). */
export type DownloadedFile = {
  file: Blob;
  fileName: string | undefined;
};

/** `attachment; filename="alocacoes-2026-10-06.xlsx"` → `alocacoes-2026-10-06.xlsx`. */
const FILE_NAME_PATTERN = /filename="([^"]+)"/;

/** Com `responseType: 'blob'` o erro também chega como `Blob`; volta a ser objeto para o envelope de erro. */
async function rethrowWithJsonBody(error: unknown): Promise<never> {
  if (isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      error.response.data = JSON.parse(await error.response.data.text());
    } catch {
      // Corpo não é JSON (proxy, rede): segue como veio e cai no erro genérico.
    }
  }
  throw error;
}

/** `GET` de arquivo (as exportações de planilha). */
export const downloadFile = (url: string, params: object): Promise<DownloadedFile> =>
  api
    .get<Blob>(url, { params, responseType: 'blob' })
    .then((r) => ({
      file: r.data,
      fileName: FILE_NAME_PATTERN.exec(String(r.headers['content-disposition'] ?? ''))?.[1],
    }))
    .catch(rethrowWithJsonBody);
